import * as demoData from './reelCreatorDemoData.mjs';

const {
  DEMO_OUTPUT_URL,
  analyseDemoProject,
  createDemoProject,
  pendingFilmingClips,
  seedDemoProjects,
  validateDemoClip
} = demoData;

const STORAGE_KEY = 'vasia-reel-creator-demo-v1';
const wait = (ms = 450) => new Promise((resolve) => setTimeout(resolve, ms));
const clone = (value) => JSON.parse(JSON.stringify(value));

const readProjects = () => {
  try {
    const saved = JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    return Array.isArray(saved) ? saved : seedDemoProjects();
  } catch {
    return seedDemoProjects();
  }
};

const writeProjects = (projects) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(projects));
  return projects;
};

const mutateProject = (projectId, updater) => {
  let updated;
  const projects = readProjects().map((project) => {
    if (project.id !== projectId) return project;
    updated = updater(clone(project));
    return updated;
  });
  if (!updated) throw new Error('Reel project was not found.');
  writeProjects(projects);
  return clone(updated);
};

export const demoReelCreatorApi = {
  async listProjects() {
    await wait(120);
    return readProjects().sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
  },

  async createReelProject() {
    const project = createDemoProject(`demo-${Date.now()}`);
    writeProjects([project, ...readProjects()]);
    await wait(180);
    return clone(project);
  },

  async getProject(projectId) {
    await wait(100);
    const project = readProjects().find((item) => item.id === projectId);
    if (!project) throw new Error('Reel project was not found.');
    return clone(project);
  },

  async uploadReference(projectId, { file, reelUrl }) {
    await wait();
    return mutateProject(projectId, (project) => ({
      ...project,
      status: 'reference_uploaded',
      reference: {
        kind: file ? 'upload' : 'link',
        name: file?.name || reelUrl,
        mimeType: file?.type || null,
        size: file?.size || null,
        assetUrl: file ? `demo-upload://${encodeURIComponent(file.name)}` : reelUrl
      },
      updatedAt: new Date().toISOString()
    }));
  },

  async analyseReference(projectId) {
    mutateProject(projectId, (project) => ({ ...project, status: 'analysing_reference' }));
    await wait(1000);
    return mutateProject(projectId, analyseDemoProject);
  },

  async uploadFilmedClip(projectId, requiredClipId, file) {
    await wait(350);
    return mutateProject(projectId, (project) => ({
      ...project,
      status: 'validating_clip',
      assets: [...(project.assets || []), {
        id: `asset-${Date.now()}`,
        requiredClipId,
        name: file.name,
        mimeType: file.type,
        size: file.size,
        createdAt: new Date().toISOString()
      }]
    }));
  },

  async validateClip(projectId, requiredClipId) {
    await wait(850);
    let validation;
    const project = mutateProject(projectId, (current) => {
      const requiredClips = current.requiredClips.map((clip) => {
        if (clip.id !== requiredClipId) return clip;
        validation = validateDemoClip(clip);
        return {
          ...clip,
          attempts: validation.attempt,
          status: validation.outcome === 'pass' ? 'approved' : 'retake',
          validation
        };
      });
      const clipsComplete = requiredClips.filter((clip) => clip.source === 'filming' && clip.status === 'approved').length;
      const remaining = requiredClips.filter((clip) => clip.source === 'filming' && clip.status !== 'approved');
      return {
        ...current,
        requiredClips,
        clipsComplete,
        status: validation.outcome === 'pass' && remaining.length === 0 ? 'filming_complete' : 'filming_required',
        updatedAt: new Date().toISOString()
      };
    });
    return { project, validation };
  },

  async startEdit(projectId, changeRequest = null) {
    await wait(300);
    return mutateProject(projectId, (project) => {
      const job = { id: `job-${Date.now()}`, status: 'queued', polls: 0, revision: Boolean(changeRequest) };
      return {
        ...project,
        status: 'edit_queued',
        job,
        changeRequests: changeRequest ? [...(project.changeRequests || []), changeRequest] : project.changeRequests,
        updatedAt: new Date().toISOString()
      };
    });
  },

  async getEditStatus(projectId) {
    await wait(180);
    return mutateProject(projectId, (project) => {
      if (!project.job || project.status === 'ready') return project;
      const polls = Number(project.job.polls || 0) + 1;
      const status = polls < 2 ? 'editing' : polls < 4 ? 'quality_check' : 'ready';
      return {
        ...project,
        status,
        job: { ...project.job, polls, status: status === 'quality_check' ? 'rendering' : status },
        finishedVideoUrl: status === 'ready' ? DEMO_OUTPUT_URL : project.finishedVideoUrl,
        updatedAt: new Date().toISOString()
      };
    });
  },

  async getFinishedVideo(projectId) {
    const project = await this.getProject(projectId);
    return { url: project.finishedVideoUrl || DEMO_OUTPUT_URL };
  },

  async requestChange(projectId, changeRequest) {
    return this.startEdit(projectId, changeRequest);
  },

  pendingFilmingClips
};
